import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appsDropdownOpen, setAppsDropdownOpen] = useState(false);
  const [gamesDropdownOpen, setGamesDropdownOpen] = useState(false);
  const [mobileCommunityOpen, setMobileCommunityOpen] = useState(false);
  const [mobileGamesOpen, setMobileGamesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chatbots', path: '/chatbots' },
    { name: 'News', path: '/news' },
  ];

  const proutApps = [
    { name: 'Cost of Living Simulator', path: 'https://claritynchaos.com/costofliving_codex/', isExternal: true },
    { name: 'Minimum Necessities Simulator', path: 'https://claritynchaos.com/minimum-necessities-social-simulator/', isExternal: true },
    { name: 'Cooperative Economy Simulator', path: 'https://claritynchaos.com/cooperative-economy-simulator/', isExternal: true },
    { name: 'Economic Democracy Simulator', path: 'https://claritynchaos.com/prout-economic-democracy-simulator/', isExternal: true },
    { name: 'Resource Utilization Simulator', path: 'https://claritynchaos.com/prout-resource-utilization-simulator/', isExternal: true },
    { name: 'Crisis Response Simulator', path: 'https://claritynchaos.com/prout-crisis-response-simulator/', isExternal: true },
    { name: 'Pramá Balance Simulator', path: 'https://claritynchaos.com/prama/', isExternal: true },
    { name: 'PROUT Social Cycle simulator', path: 'https://claritynchaos.com/prout-social-cycle-simulator/', isExternal: true },
    { name: 'PROUT Society Simulator', path: 'https://claritynchaos.com/prout-society-simulator/', isExternal: true },
    { name: 'From Listening to Movement', path: 'https://claritynchaos.com/listening_movement/', isExternal: true },
    { name: 'WelfareLens', path: 'https://claritynchaos.com/welfarelens/', isExternal: true },
    { name: 'Causal Layered Analysis', path: 'https://claritynchaos.com/cla-explorer-transformative-futures-tool/', isExternal: true },
    { name: 'Claim Verification Assessor', path: 'https://claritynchaos.com/Claim_Verification_Assessor/', isExternal: true },
    { 
      name: 'COMMUNITY APPS', 
      isSubmenu: true, 
      links: [
        { name: 'SHG Companion', path: 'https://claritynchaos.com/SHG_Companion/', isExternal: true },
        { name: 'Download SHG Companion', path: 'https://claritynchaos.com/SHG_Companion/shg_companion.zip', isExternal: true }
      ] 
    },
  ];

  const proutGames = [
    { name: 'Lokasamgraha', path: 'https://claritynchaos.com/lokasamgraha_game/', isExternal: true },
    { name: 'The Sarkar Game', path: 'https://claritynchaos.com/The_Sarkar_Game/', isExternal: true },
    { name: 'Samaja Cakra Civilisation', path: 'https://claritynchaos.com/Samaja_Cakra_02/', isExternal: true },
  ];

  const isProutActive = proutApps.some(link => !link.isExternal && location.pathname === link.path);
  const isGamesActive = proutGames.some(link => !link.isExternal && location.pathname === link.path);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-black/80 backdrop-blur-md py-4 border-b border-white/5' : 'bg-transparent py-6'}`} >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold tracking-tighter text-white hover:text-cyan-200 transition-colors">
          Clarity<span className="text-cyan-400 font-light">in</span>Chaos
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center space-x-8">
          {mainLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path} 
              className={`relative text-sm uppercase tracking-widest transition-colors duration-300 ${location.pathname === link.path ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`}
            >
              {link.name}
              {location.pathname === link.path && (
                <motion.span layoutId="underline" className="absolute left-0 -bottom-1 w-full h-[1px] bg-cyan-400 shadow-[0_0_15px_rgba(34,211,238,1)]" />
              )}
            </Link>
          ))}

          {/* PROUT Apps Dropdown */}
          <div 
            className="relative group"
            onMouseEnter={() => setAppsDropdownOpen(true)}
            onMouseLeave={() => setAppsDropdownOpen(false)}
          >
            <button className={`flex items-center space-x-1 text-sm uppercase tracking-widest transition-colors duration-300 ${isProutActive ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`}>
              <span>PROUT Apps</span>
              <FiChevronDown className={`transition-transform duration-300 ${appsDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {appsDropdownOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-80 bg-dark-800/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-visible shadow-2xl"
                >
                  <div className="py-2">
                    {proutApps.map((link) => (
                      link.isSubmenu ? (
                        <div key={link.name} className="relative group/sub">
                          <button className="flex items-center justify-between w-full px-6 py-3 text-xs uppercase tracking-widest text-gray-400 hover:text-cyan-400 hover:bg-white/5 transition-all">
                            <span>{link.name}</span>
                            <FiChevronRight />
                          </button>
                          <div className="absolute left-full top-0 hidden group-hover/sub:block w-72 bg-dark-800/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-2xl ml-1">
                            {link.links.map((subLink) => (
                              <a 
                                key={subLink.name} 
                                href={subLink.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-6 py-3 text-xs uppercase tracking-widest text-gray-400 hover:text-cyan-400 hover:bg-white/5 transition-all"
                              >
                                {subLink.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : link.isExternal ? (
                        <a 
                          key={link.name} 
                          href={link.path} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-6 py-3 text-xs uppercase tracking-widest text-gray-400 hover:text-cyan-400 hover:bg-white/5 transition-all"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link 
                          key={link.name} 
                          to={link.path} 
                          className={`block px-6 py-3 text-xs uppercase tracking-widest transition-all ${location.pathname === link.path ? 'text-cyan-400 bg-white/5' : 'text-gray-400 hover:text-cyan-400 hover:bg-white/5'}`}
                        >
                          {link.name}
                        </Link>
                      )
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* PROUT Games Dropdown */}
          <div 
            className="relative group"
            onMouseEnter={() => setGamesDropdownOpen(true)}
            onMouseLeave={() => setGamesDropdownOpen(false)}
          >
            <button className={`flex items-center space-x-1 text-sm uppercase tracking-widest transition-colors duration-300 ${isGamesActive ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`}>
              <span>PROUT Games</span>
              <FiChevronDown className={`transition-transform duration-300 ${gamesDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {gamesDropdownOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-64 bg-dark-800/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-2xl"
                >
                  <div className="py-2">
                    {proutGames.map((link) => (
                      <a 
                        key={link.name} 
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-6 py-3 text-xs uppercase tracking-widest text-gray-400 hover:text-cyan-400 hover:bg-white/5 transition-all"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-2xl text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} >
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 overflow-hidden" 
          >
            <div className="flex flex-col p-6 space-y-6">
              <div className="flex flex-col space-y-4">
                {mainLinks.map((link) => (
                  <Link 
                    key={link.name} 
                    to={link.path} 
                    onClick={() => setMobileMenuOpen(false)} 
                    className={`text-lg font-medium tracking-wide ${location.pathname === link.path ? 'text-cyan-400' : 'text-gray-300'}`} 
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              
              {/* Mobile PROUT Apps */}
              <div className="pt-4 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">PROUT Apps</p>
                <div className="flex flex-col space-y-4">
                  {proutApps.map((link) => (
                    link.isSubmenu ? (
                      <div key={link.name} className="space-y-4">
                        <button 
                          onClick={() => setMobileCommunityOpen(!mobileCommunityOpen)}
                          className="flex items-center justify-between w-full text-base font-medium text-gray-400"
                        >
                          <span>{link.name}</span>
                          <FiChevronDown className={`transition-transform duration-300 ${mobileCommunityOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {mobileCommunityOpen && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="flex flex-col space-y-4 pl-4 border-l border-white/10"
                            >
                              {link.links.map((subLink) => (
                                <a 
                                  key={subLink.name} 
                                  href={subLink.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="text-sm font-medium text-gray-500 hover:text-cyan-400"
                                >
                                  {subLink.name}
                                </a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <a 
                        key={link.name} 
                        href={link.path} 
                        target={link.isExternal ? "_blank" : undefined}
                        rel={link.isExternal ? "noopener noreferrer" : undefined}
                        onClick={() => setMobileMenuOpen(false)} 
                        className="text-base font-medium text-gray-400 hover:text-cyan-400" 
                      >
                        {link.name}
                      </a>
                    )
                  ))}
                </div>
              </div>

              {/* Mobile PROUT Games */}
              <div className="pt-4 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">PROUT Games</p>
                <div className="flex flex-col space-y-4">
                  <button 
                    onClick={() => setMobileGamesOpen(!mobileGamesOpen)}
                    className="flex items-center justify-between w-full text-base font-medium text-gray-400"
                  >
                    <span>Games List</span>
                    <FiChevronDown className={`transition-transform duration-300 ${mobileGamesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileGamesOpen && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-col space-y-4 pl-4 border-l border-white/10"
                      >
                        {proutGames.map((link) => (
                          <a 
                            key={link.name} 
                            href={link.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileMenuOpen(false)}
                            className="text-sm font-medium text-gray-500 hover:text-cyan-400"
                          >
                            {link.name}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;